import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, Calendar } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { Seo } from "@/components/Seo";
import { getArticle, ARTICLES } from "@/data/articles";

const SITE = "https://gestimmony.fr";

// Rendu inline minimal : **gras**.
function inline(text) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={i} className="font-semibold text-slate-900">{p.slice(2, -2)}</strong>
      : <span key={i}>{p}</span>
  );
}

function Block({ b }) {
  switch (b.t) {
    case "h2":
      return <h2 className="font-heading text-2xl font-bold text-slate-900 mt-10 mb-3">{b.text}</h2>;
    case "p":
      return <p className="text-slate-700 leading-relaxed my-4 text-[17px]">{inline(b.text)}</p>;
    case "ul":
      return (
        <ul className="list-disc pl-6 space-y-2 my-4 text-slate-700 text-[17px]">
          {b.items.map((it, i) => <li key={i}>{inline(it)}</li>)}
        </ul>
      );
    case "ol":
      return (
        <ol className="list-decimal pl-6 space-y-2 my-4 text-slate-700 text-[17px]">
          {b.items.map((it, i) => <li key={i}>{inline(it)}</li>)}
        </ol>
      );
    case "callout":
      return (
        <div className="my-6 rounded-xl border-l-4 border-[hsl(var(--primary))] bg-teal-50/60 p-4">
          {b.title && <div className="font-heading font-semibold text-teal-900 mb-1">{b.title}</div>}
          <div className="text-slate-700 text-[15px] leading-relaxed">{inline(b.text)}</div>
        </div>
      );
    case "table":
      return (
        <div className="my-6 overflow-x-auto">
          <table className="w-full text-sm border border-stone-200 rounded-lg overflow-hidden">
            <thead className="bg-stone-50">
              <tr>{b.head.map((h, i) => <th key={i} className="text-left font-semibold text-slate-700 px-4 py-2.5 border-b border-stone-200">{h}</th>)}</tr>
            </thead>
            <tbody>
              {b.rows.map((r, ri) => (
                <tr key={ri} className="odd:bg-white even:bg-stone-50/50">
                  {r.map((c, ci) => <td key={ci} className="px-4 py-2.5 text-slate-700 border-b border-stone-100">{inline(c)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    default:
      return null;
  }
}

export default function ArticlePage() {
  const { slug } = useParams();
  const article = getArticle(slug);
  if (!article) return <Navigate to="/ressources" replace />;

  const url = `${SITE}/ressources/${article.slug}`;
  const others = ARTICLES.filter((a) => a.slug !== article.slug).slice(0, 2);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline: article.title,
        description: article.metaDescription,
        image: article.cover,
        datePublished: article.date,
        dateModified: article.date,
        author: { "@type": "Organization", name: "Gestimmony" },
        publisher: {
          "@type": "Organization",
          name: "Gestimmony",
          logo: { "@type": "ImageObject", url: `${SITE}/favicon.png` },
        },
        mainEntityOfPage: { "@type": "WebPage", "@id": url },
      },
      {
        "@type": "FAQPage",
        mainEntity: article.faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="article-page">
      <Seo
        title={article.metaTitle}
        description={article.metaDescription}
        path={`/ressources/${article.slug}`}
        image={article.cover}
        type="article"
        jsonLd={jsonLd}
      />

      <header className="border-b border-stone-200 bg-white/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <LogoMark className="h-8" />
            <span className="font-heading font-bold">Gestimmony</span>
          </Link>
          <Link to="/ressources" className="text-sm text-slate-600 hover:text-[hsl(var(--primary))] inline-flex items-center gap-1.5">
            <ArrowLeft className="h-4 w-4" /> Ressources
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <nav className="text-xs text-slate-400 mb-6" aria-label="Fil d'Ariane">
          <Link to="/ressources" className="hover:text-[hsl(var(--primary))]">Ressources</Link>
          <span className="mx-1.5">/</span>
          <span className="text-slate-500">{article.category}</span>
        </nav>

        <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
          <span className="font-medium text-teal-700">{article.category}</span>
          <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {article.dateLabel}</span>
          <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {article.readTime}</span>
        </div>

        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 leading-tight" data-testid="article-title">
          {article.title}
        </h1>

        <div className="mt-7 rounded-2xl overflow-hidden border border-stone-200">
          <img src={article.cover} alt={article.title} className="w-full aspect-[16/9] object-cover" />
        </div>

        <article className="mt-8">
          {article.content.map((b, i) => <Block key={i} b={b} />)}
        </article>

        <section className="mt-12" data-testid="article-faq">
          <h2 className="font-heading text-2xl font-bold text-slate-900 mb-4">Questions fréquentes</h2>
          <div className="space-y-3">
            {article.faq.map((f, i) => (
              <div key={i} className="rounded-xl border border-stone-200 bg-white p-4">
                <div className="font-heading font-semibold text-slate-900">{f.q}</div>
                <p className="mt-1.5 text-slate-600 text-[15px] leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        <div className="mt-12 rounded-2xl bg-[hsl(var(--primary))] text-white p-7 sm:p-8">
          <h3 className="font-heading text-xl font-bold">Gérez vos locations sans Excel</h3>
          <p className="mt-2 text-white/80">
            Révision IRL, quittances, 2044 : Gestimmony automatise tout ça. Essai gratuit, sans engagement.
          </p>
          <Link to="/register" data-testid="article-cta-register" className="mt-4 inline-flex items-center gap-2 bg-white text-[hsl(var(--primary))] font-semibold rounded-full px-6 py-3 hover:bg-stone-100 transition-colors">
            Créer un compte gratuit <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {others.length > 0 && (
          <section className="mt-14">
            <h2 className="font-heading text-lg font-bold text-slate-900 mb-4">À lire aussi</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              {others.map((a) => (
                <Link key={a.slug} to={`/ressources/${a.slug}`} data-testid={`related-${a.slug}`} className="group flex gap-3 rounded-xl border border-stone-200 bg-white p-3 hover:shadow-md transition-all">
                  <img src={a.cover} alt={a.title} loading="lazy" className="h-16 w-20 rounded-lg object-cover shrink-0" />
                  <div>
                    <div className="text-xs text-teal-700 font-medium">{a.category}</div>
                    <div className="font-heading font-semibold text-slate-900 text-sm leading-snug group-hover:text-[hsl(var(--primary))] transition-colors">{a.title}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
